<template>
  <div
    class="template-preview"
    :class="{ 'template-preview --loading': !templateData, 'not-modal': isNotModal }"
  >
    <TemplateDetails
      class="flex-none"
      :templateData="templateData"
      v-if="templateData"
      :templateLocation="templateLocation"
      :copyType="copyType"
      :isNotModal="isNotModal"
      :onCategoryChange="onCategoryChange"
    />
    <TemplateSlides
      class="flex-grow"
      :isPopupMode="isPopupMode"
      :slides="templateData.Slides"
      v-if="templateData"
    />
    <InlineLoader v-else additionalClasses="logo-loader" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as Sentry from '@sentry/vue'

import InlineLoader from '@/components-v2/common/loading/InlineLoader.vue'
import TemplateDetails from './TemplateDetails.vue'
import TemplateSlides from './TemplateSlides.vue'
import ApiTemplate from '@/api/template'
import Utils from '@/util'
import cookie from '@/services/cookie'
import eventCategories from '@/constant/eventCategories'
import EventBus from '@/eventBus'
import Constants from '@/constant'

export default {
  components: {
    TemplateDetails,
    TemplateSlides,
    InlineLoader,
  },
  props: {
    presentationShareCode: {
      type: String,
      required: false,
      default: '',
    },
    isPopupMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    templateLocation: {
      type: String,
      default: null,
    },
    where: {
      type: String,
      default: '',
    },
    copyType: {
      type: String,
      default: '',
    },
    isNotModal: {
      type: Boolean,
      default: false,
    },
    onCategoryChange: {
      type: Function,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      templateData: null,
    }
  },
  computed: {
    ...mapGetters('featureToggles', [
      'canMergePickImageIntoPickAnswer',
    ]),
  },
  async mounted() {
    let shareCode
    if (!this.presentationShareCode) {
      shareCode = this.$route.params.shareCode
    }
    await this.getPresentationTemplateDetail(shareCode)
    if (this.templateData) {
      this.track()
    }
    EventBus.$on('update-template-preview-after-published', (template) => {
      this.templateData.name = template.name
      this.templateData.description = template.description
      if (template.customThumbnailImage !== '_blank') {
        this.templateData.customThumbnailImage = template.customThumbnailImage
      }
      if (template.categoryIds) { // categories modal in template library
        const presentationsCategories = template.categoryIds.map((item) => {
          return {
            categoryId: item,
            presentationId: this.templateData.id,
          }
        })
        this.templateData.PresentationsCategories = presentationsCategories
      }
    })
  },
  destroyed() {
    EventBus.$off('update-template-preview-after-published')
  },

  methods: {
    ...mapActions([
      'setCanEditPresentation',
    ]),
    track() {
      const TEMPLATE_LIBRARY = 'Template library'
      let trigger = this.templateLocation === 'top panel' ? 'Presentation list' : TEMPLATE_LIBRARY
      if (trigger === TEMPLATE_LIBRARY && this.where) {
        trigger = this.where
      }
      Utils.mixpanelTracking(eventCategories.TEMPLATE_LIBRARY, 'Preview template', trigger, this.templateData.id)
    },
    async getPresentationTemplateDetail(shareCode) {
      try {
        const resp = await ApiTemplate.getTemplate(shareCode || this.presentationShareCode)

        resp.Slides.sort((a, b) => {
          return a.order - b.order
        })
        resp.Slides.forEach((item) => {
          item.typeAnswerOptions = []
          if (item.correctQuizTypeAnswer) {
            item.typeAnswerOptions.push({
              index: null,
              title: item.correctQuizTypeAnswer,
            })
          }
          if (item.otherCorrectQuiz) {
            item.typeAnswerOptions =
              item.typeAnswerOptions.concat(JSON.parse(item.otherCorrectQuiz))
          }
          if (this.canMergePickImageIntoPickAnswer) {
            if (item.type === Constants.SLIDE_TYPE.PICK_ANSWER && !item.slideType) {
              item.slideType = Constants.SLIDE_TYPE.PICK_IMAGE
            }
          }
        })
        this.templateData = resp
        const userId = cookie.getValue('aha-user-id')
        if (+userId === +resp.TakenOverByUser.id) {
          this.setCanEditPresentation(true)
        }
      } catch (error) {
        Sentry.captureException({
          error: `template preview ${error}`,
          shareCode,
          presentationShareCode: this.presentationShareCode,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.template-preview {
  position: relative;
  min-height: 300px;
  @apply flex flex-col bg-base-0 md:flex-row;
  @apply items-center md:items-start;

  @media screen and (max-width: 767px) {
    padding-top: 30px;
    height: 100vh;
  }

  &.--loading {
    @apply items-center justify-center;
  }

  &.not-modal {
    background-color: transparent
  }

}
.logo-loader {
  position: absolute;
  top: 50%;
  left: 50%;
  text-align: center;
  transform: translate(-50%, -50%);
}
</style>
